/**
 * ## Mini Component
 * 
 * The Mini component establishes communication between the parent frame and the child iframe so
 * that the parent's environment can be ertrieved and lifted into the child's environment.
 * 
 * The window.postMessage method is used because browser's do not allow direct access from a child iframe to its 
 * parent (unless they are on exactly the same domain).
 * [MDN postMessage Documentation](https://developer.mozilla.org/en-US/docs/Web/API/Window/postMessage)
 * 
 * @category Route
 * @module Mini Path Route
 */
import { useCallback, useEffect, useRef, useState } from "react";

import PauseDialog from "../containers/PauseDialog";
import TokenFountain from "../containers/TokenFountain";
import Activity from "../containers/activity/Activity";
import PathTitle, { CharacterType, SceneType } from "../containers/PathTitle";
import { setOpen } from "../slices/pauseDialogSlice"
import { loadActivity, setLoading, setActivityId, setShouldShowFinalReward, setIsActivityJSONFirstLoad, setCurrentLevel } from "../slices/activitySlice";
import { setIsButtonBlocked } from "../slices/buttonSlice";
// import { setDebugSubject, setDebugCharacter, setDebugScene } from "../slices/miniSlice";
import { setDoesNeedRendering, setScreenSize, setSkin, showGameButtons, setAreGameButtonsCollapsed, setRestartDialogOpen, setIsPathComplete, setIsRefreshing} from "../slices/miniSlice";
import { setIsPortrait } from "../slices/landscapeOnlyDialogSlice";
import { setAnchorLevelByCurrentLevel } from "../slices/navigationSlice";
import { setIsOpen } from "../slices/pathTitleSlice";
import { useAppDispatch, useAppSelector } from "../hooks/hooks";
import { sendMessage } from "../lib/iFrameCommunications";
import { isAppleSafari, isAppleIOS } from "../lib/urlTools";
import { IMiniPathConfig, IMiniPathConfigEntry } from "../types/types"
import miniPathConfig from "../config/mini.json";
import { ActivityType } from "../components/ActivityUI";
import SZGameButtons from "../components/SZGameButtons";
import { ISZButtonSettings } from "../containers/activity/utils/activityUtils";
import FinalRewardVideoPlayer from "../components/FinalRewardVideoPlayer";
import DemoVideoPlayer from "../components/DemoVideoPlayer";
import RestartPathDialog from "../containers/RestartPathDialog";
import config from "../config/config";
import _ from "lodash";

const Mini = () => {
	const dispatch = useAppDispatch();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const { areAllScriptsLoaded, isLoaded, isLoading, isActivityJSONFirstLoad, isFirstGameLoad, isFinalRewardTime, isDemoTime } = useAppSelector((state) => state.activity);
	let parentData = useAppSelector((state) => state.activity.parent);
	let { areGameButtonsVisible, activityType, doesNeedRendering, videoSrcWidth, videoSrcHeight, restartDialogOpen, isFirstActivityInSession, isRefreshing } = useAppSelector((state) => state.mini);
    // let { debugSubject, debugScene, debugCharacter } = useAppSelector((state) => state.mini);
    const miniConfig: IMiniPathConfig = miniPathConfig;
    const hasVideoPlayed = useAppSelector((state) => state.time.hasPlayed);
    const activityJSON = useAppSelector((state) => state.activity.json);
    const activityId = useAppSelector((state) => state.activity.activityId);
    let tempFlagRequiresHTMLButtonPadding = useRef(false);

    const {screenWidth, pathBaseWidth} = useAppSelector((state) => state.mini);

    const shouldDisplaySpinner = () => {
        if(activityType === ActivityType.Video || activityType === ActivityType.Song ) {
            return (isLoading && !isLoaded)
        } else {
            return (isFirstGameLoad || (isLoading && !isLoaded));
        }
    }

    const [isWritableVisible, setIsWritableVisible] = useState(false);
    const [isWritableMobile, setIsWritableMobile] = useState(false);
    const [writableLeftOverride, setWritableLeftOverride] = useState<number>(0);
    const [whiteAreaWidth, setWhiteAreaWidth] = useState<number>(0);
    const [activitiesVisited, setActivitiesVisited] = useState<number>(0);
    const [prevActivityId, setPrevActivityId] = useState<number>(0);

    const colorPaletteUiId = `writables-ui`;
    const colorPaletteButtonId = `writables-ui-button-inside`;
    const colorPaletteInnerId = `writables-ui-inner`;

    const updatePaletteData = useCallback(() => {
        const configEntry = activityJSON?.data?.config?.[0];
        if (!configEntry) return;

        if (configEntry.indexOf("path-writable") > -1) {
            setIsWritableVisible(Boolean(window.szWritables?.data?.displayed));
            setIsWritableMobile(Boolean(window.szWritables?.data?.mobile));
        } else if (configEntry.indexOf("my-story-book") > -1) {
            const tray: any = (window as any).SZ?.PaintToolsTray?.instance;
            const displayed = Boolean(tray?._displayed);
            const mobile = Boolean(tray?._mobile);
            setIsWritableVisible(displayed);
            setIsWritableMobile(mobile);
        }
    }, [activityJSON]);

    useEffect(() => {
        let pathData = activityJSON.data?.pathData;
        if(pathData && isActivityJSONFirstLoad && activityJSON.data !== undefined){
            let isPathComplete = true;
            pathData.forEach((data: any) => {
                if(!Number(data.finished)){
                    isPathComplete = false;
                }
            }, this);

            if(isPathComplete){
                dispatch(setShouldShowFinalReward(false));
                dispatch(setRestartDialogOpen(true));                
                dispatch(setIsPathComplete(true)); 
            }

            dispatch(setIsActivityJSONFirstLoad(false));
        }
    }, [activityJSON, dispatch, isActivityJSONFirstLoad]);

    useEffect(() => {
        tempFlagRequiresHTMLButtonPadding.current = activityJSON?.data?.miniPathButtonPadding; 
    }, [activityJSON]);

    useEffect(() => {
        const MINI_PATH_REFRESH_THRESHOLD = 3;

        if(activityId !== undefined && activityId !== prevActivityId) {
            setActivitiesVisited(activitiesVisited + 1);
        }
        setPrevActivityId(activityId);

        let shouldRefresh = false;
        if(parentData?.settings?.sz_mini?.levelsToRefreshOnVisit?.length > 0) {
            if(_.includes(parentData?.settings?.sz_mini?.levelsToRefreshOnVisit, parentData?.settings?.szLearningPathData.currentLevel) && isFirstActivityInSession === false){
                shouldRefresh = true;
            }
        } else {
            if ((activitiesVisited + 1) > MINI_PATH_REFRESH_THRESHOLD) {
                shouldRefresh = true;
            }
        }

        if(isFinalRewardTime){
            shouldRefresh = false;
        }

        if(shouldRefresh) {
            // Save current level so it can be retreived after page refresh
            if(parentData !== undefined && parentData.settings !== undefined && parentData.settings.szLearningPathData !== undefined){
                let currentLevel = parentData?.settings?.szLearningPathData.currentLevel;
                localStorage.setItem(config.LOCAL_STORAGE_KEYS.forced_refresh_next_level, currentLevel);
            }

            if(!isRefreshing){
                sendMessage("REFRESH MINI");
                dispatch(setIsRefreshing(true));
            }
        }
    }, [activitiesVisited, activityId, parentData, prevActivityId, isFinalRewardTime, isRefreshing, isFirstActivityInSession, dispatch]);

    const determineWritableOffset = useCallback(() => {
        // Center writables ui for mini path
        let writablesInner = document.getElementById(colorPaletteInnerId);
        if(writablesInner){
            setWhiteAreaWidth(screenWidth - pathBaseWidth);
            let newLeft = (whiteAreaWidth - writablesInner?.offsetWidth) / 2;
            setWritableLeftOverride(newLeft);
        }
    }, [colorPaletteInnerId, screenWidth, pathBaseWidth, whiteAreaWidth])
    
    useEffect(() => {
        if(isWritableVisible){
            determineWritableOffset();
        }
    }, [determineWritableOffset, isWritableVisible]);

    // Check if this activity is a 'Path Writable' or "My Story Book"
    // If so watch for relevant html elements via observers and help format color palette for mini path layout
    // Ensure cleanup of html, observer, and stateChangeHandler
    useEffect(() => {
        const configEntry: string | undefined = activityJSON?.data?.config?.[0];
        const isWritableActivity: boolean =
            !!configEntry &&
            (configEntry.indexOf("path-writable") > -1 || configEntry.indexOf("my-story-book") > -1);

        // Only wire this up for writable / my-story-book activities
        if (!areAllScriptsLoaded || !isWritableActivity) {
            return;
        }

        const szGame: any = (window as any).szGame;
        if (!szGame || !szGame.game || !szGame.game.state) {
            // szGame not ready, skipping writable state listener
            return;
        }

        // Initial sync for this activity
        updatePaletteData();
        determineWritableOffset();

        const stateChangeHandler = (newStateName: string, oldStateName: string) => {
            if (oldStateName === "Preloader") {
                const mediaWrapperObserverConfig = { childList: true, subtree: true };

                window.wrapperObserver = new MutationObserver((mutationsList: MutationRecord[], observer: MutationObserver) => {
                    for (const mutation of mutationsList) {
                        if (mutation.type === "childList") {
                            for (const node of mutation.addedNodes) {
                                if (
                                    node.nodeType === Node.ELEMENT_NODE &&
                                    (node as any).id === colorPaletteButtonId
                                ) {
                                    const paletteButton = node as HTMLElement;

                                    updatePaletteData();

                                    const buttonObserverConfig = { attributes: true, childList: false, subtree: false };
                                    window.writablesObserver = new MutationObserver(() => {
                                        updatePaletteData();
                                    });
                                    window.writablesObserver.observe(paletteButton, buttonObserverConfig);

                                    if (window.wrapperObserver) {
                                        window.wrapperObserver.disconnect();
                                    }
                                }
                            }
                        }
                    }
                });

                const mediaWrapper = document.getElementById("media-wrapper");
                if (mediaWrapper && window.wrapperObserver) {
                    window.wrapperObserver.observe(mediaWrapper, mediaWrapperObserverConfig);
                }
            }
        };

        // Register our handler for this activity
        szGame.game.state.onStateChange.add(stateChangeHandler, null);
    }, [activityJSON, areAllScriptsLoaded, colorPaletteButtonId, updatePaletteData, determineWritableOffset]);

    // Bootstraps the mini experience on first load: kicks off loading, wires a resize handler,
    // starts parent-frame communications, and opens the title overlay on first visit.
    useEffect(() => {
        if (isLoaded || isLoading) {
            // Either already loaded or currently loading; nothing to kick off.
            return;
        }

        // We're idle: start loading process
        dispatch(setLoading());
        dispatch(setIsButtonBlocked(true));

        const resizeHandler = () => {
            dispatch(setIsOpen(false));
            dispatch(setIsButtonBlocked(false));
            dispatch(showGameButtons());
            dispatch(setDoesNeedRendering(true));
        };

        window.addEventListener("resize", resizeHandler);

        window.szLogger.log("%c Establish Communications", "background-color: pink; color: black");

        // Start communications with parent frame
        sendMessage("START COMMUNICATIONS");
        if (isAppleSafari() || isAppleIOS()) {
            setTimeout(() => {
                if (!isLoaded) {
                    sendMessage("START COMMUNICATIONS");
                }
            }, 500);
        }

        // Open the title overlay on first visit
        if (parentData?.settings?.szLearningPathData.pathFirstVisit) {
            dispatch(setIsOpen(true));
        }

        // Cleanup: remove our resize handler when we leave this "bootstrapping" state or unmount
        return () => {
            window.removeEventListener("resize", resizeHandler);
        };
    }, [dispatch, isLoaded, isLoading, parentData]);

    // Clears the "doesNeedRendering" flag once the game is actually loaded,
    // so we only trigger extra renders when they're really needed.
    useEffect(() => {
        if (doesNeedRendering && isLoaded) {
            dispatch(setDoesNeedRendering(false));
        }
    }, [doesNeedRendering, isLoaded, dispatch]);

    // Tracks orientation and viewport changes to maintain portrait/landscape state,
    // and also pauses/resumes the inline video player when the window loses or gains focus.
    useEffect(() => {
        const blockPotraitMode = () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            if (width > 1000 || width > height) {
                dispatch(setIsPortrait(false));
            } else {
                dispatch(setIsPortrait(true));
                dispatch(setOpen(false));
            }
        }

        window.addEventListener("resize", () => {
            blockPotraitMode();
        });

        // Video Player pause on blur event
        window.addEventListener("blur", function() {
            if (hasVideoPlayed && document.getElementById("sz-media-player")) {
                const videoPlayer: HTMLVideoElement = document.getElementById("sz-media-player") as HTMLVideoElement;
                videoPlayer.pause();
            }
        });

        // Video Player play on focus event
        window.addEventListener("focus", function() {
            if (hasVideoPlayed && document.getElementById("sz-media-player")) {
                const videoPlayer: HTMLVideoElement = document.getElementById("sz-media-player") as HTMLVideoElement;
                videoPlayer.play();
            }
        });

        setTimeout(() => {
            blockPotraitMode();
        }, 10);
    });

    // Listens for postMessage events from the parent and, while we're in the loading phase,
    // hydrates Redux with the parent environment and selects the correct activity/level.
    useEffect(() => {
        const listener = (event: MessageEvent) => {
            // Do we trust the sender of this message?
            if (
                event.origin !== "https://beta.anywhereteacher.com"
                && event.origin !== "https://dev.mini.anywhereteacher.com"
                && event.origin !== "https://mini.anywhereteacher.com"
                && event.origin !== "https://anywhereteacher.com"
                && event.origin !== "http://localhost:8082"
            ) {
                return;
            }

            // Handle communications from the parent and process the environment settings
            // Only react while we're in the "loading" phase
            if (!isLoaded && isLoading) {
                const raw = event.data;
                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;

                window.szLogger.log("Child Listener: Parent Environment", parsed);

                const settings = parsed;
                dispatch(loadActivity({ settings }));
                dispatch(setAreGameButtonsCollapsed(false));

                let curLevel = settings.szLearningPathData.currentLevel;
                if (localStorage.getItem(config.LOCAL_STORAGE_KEYS.forced_refresh_next_level) !== null) {
                    curLevel = Number(localStorage.getItem(config.LOCAL_STORAGE_KEYS.forced_refresh_next_level));
                    requestAnimationFrame(() => {
                        localStorage.removeItem(config.LOCAL_STORAGE_KEYS.forced_refresh_next_level);
                    });
                }

                dispatch(setCurrentLevel(curLevel));
                dispatch(setActivityId(settings.szLearningPathData.activities[curLevel][0].nid));
                dispatch(setAnchorLevelByCurrentLevel(curLevel));
            }
        };

        window.addEventListener("message", listener);
        // Cleanup: ALWAYS remove this exact function when deps change or component unmounts
        return () => {
            window.removeEventListener("message", listener);
        };
    }, [dispatch, isLoaded, isLoading]);

    /**
     * Set the body styling only when the it's a fresh load of a new game
     */
    if (isFirstGameLoad) {
        document.getElementById("mini-body")!.style.backgroundColor = "#000000";
        document.getElementById("mini-body")!.style.overflow = "hidden";
    }

    const screenLayout = (skin: IMiniPathConfigEntry) => {
        dispatch(setSkin(skin));

        // Source dimens
        const pathBaseSrcWidth = 400;
        const pathBaseSrcHeight = 1440;
        const strokeSourceWidth = 12;

        const innerWidth = window.innerWidth;
        const innerHeight = window.innerHeight;
        // Phaser activity src dimens. Set default dimens since the activity my not exist yet
        let activitySrcWidth = (window.szGame?.game?.width) ? window.szGame?.game?.width : 800; //1280;
        let activitySrcHeight = (window.szGame?.game?.height) ? window.szGame?.game?.height : 600; //720;

        // To optimize layout for videos, retrieve the video src dimensions to use as the activitySrc dimens
        if(activityType === ActivityType.Video || activityType === ActivityType.Song ) {
            if(videoSrcWidth && videoSrcHeight){
                activitySrcWidth = videoSrcWidth;
                activitySrcHeight = videoSrcHeight;
            }
        }

        // Calculate the scale needed to bring the phaser activity to the same scale as the path base
        const tempScaleFactor = pathBaseSrcHeight / activitySrcHeight;
        let activityScaledWidth: number = activitySrcWidth * tempScaleFactor;
        let activityScaledHeight: number = activitySrcHeight * tempScaleFactor;

        // Find the dimens required for both the path base AND the activity content
        const fullContentWidth = pathBaseSrcWidth + activityScaledWidth;
        const fullContentHeight = pathBaseSrcHeight;
        const fullContentAspectRatio = fullContentWidth / fullContentHeight;

        // With both the pathBase and the activity at the same scale, now determine the final fixed aspect ratio
        const screenAspectRatio = innerWidth / innerHeight;
        let miniContentWidth = (screenAspectRatio > fullContentAspectRatio) ? innerHeight * fullContentAspectRatio : innerWidth;

        // Some activities may require a right margin for the html buttons so that they do not cover activity functionality
        if(tempFlagRequiresHTMLButtonPadding.current && window?.szButtons?.data){
            let settings: ISZButtonSettings = window.SZButtons.prototype.determineSettings();
            miniContentWidth -= settings.buttonWidth + (2 * settings.buttonSpacing);
        }
        const miniContentHeight = miniContentWidth / fullContentAspectRatio;

        // Determine ratio to scale any source dimens to the miniContent dimens
        let scaleRatio = miniContentWidth / fullContentWidth;
        // Calculate final dimentions based on the scaleRatio
        const renderPathWidth = (pathBaseSrcWidth * scaleRatio);
        const renderActivityWidth = (activityScaledWidth * scaleRatio);
        const renderActivityHeight = (activityScaledHeight * scaleRatio);
        const strokeRenderWidth = (strokeSourceWidth * scaleRatio);

        // Determine the horizontal position of the activity
        let activityWrapperLeft = renderPathWidth + (innerWidth - renderPathWidth - renderActivityWidth) / 2;
        if(tempFlagRequiresHTMLButtonPadding.current && window?.szButtons?.data) {
            let settings: ISZButtonSettings = window.SZButtons.prototype.determineSettings();
            activityWrapperLeft = renderPathWidth + (innerWidth - renderPathWidth - renderActivityWidth - settings.buttonWidth - settings.buttonSpacing) / 2;
        }

        /**
         * Save all the scaled dimensions
         */
        dispatch(setScreenSize({
            newScaleRatio: scaleRatio,
            screenWidth: innerWidth, 
            screenHeight: innerHeight,
            contentWidth: miniContentWidth,
            contentHeight: miniContentHeight,
            pathBaseWidth: renderPathWidth, 
            activityWidth: renderActivityWidth,
            strokeWidth: strokeRenderWidth
        }));

        /**
         * ### Animate SZ Game Button Show/Hide
         * 
         * @param wrapper       reference to the wrapper
         * @param start         starting opacity of the animation
         * @param end           ending opacity of the animation
         */
        const animateWrapper = (wrapper: HTMLDivElement, start: number, end: number) => {
            wrapper.animate(
                [
                    { opacity: start },
                    { opacity: end }
                ],
                {
                    easing: "linear",
                    iterations: 1,
                    duration: 300
                }
            );
            wrapper.style.top = end.toString();
        }

        /**
         * handle showing and hiding animation of SZ Game Button wrapper
         */
        if (wrapperRef.current) {
            if (areGameButtonsVisible && wrapperRef.current.style.opacity === "0") {
                animateWrapper(wrapperRef.current, 0, 1);
            } else if (!areGameButtonsVisible && wrapperRef.current.style.opacity === "1") {
                animateWrapper(wrapperRef.current, 1, 0);
            }
        }

        return (
            <div id="mini-base" 
                style={{
                    position: "relative",
                    width: innerWidth,
                    height: innerHeight,
                    overflow: "hidden",
                }}
            >
                <div id="mini-scripts"></div>

                { restartDialogOpen && <RestartPathDialog/>}

                { isDemoTime && <DemoVideoPlayer/> }

                { !isFinalRewardTime && <div id="mini-content"
                    style={{
                        position: "relative",
                        top:( innerHeight - miniContentHeight) / 2,
                        left: 0,
                        width: innerWidth,
                        height: miniContentHeight,
                        marginLeft: "auto",
                        marginRight: "auto"
                    }}>

                    <PathTitle 
                        skin={skin}
                        icon={parentData.settings.szLearningPathData.subject}
                    />

                    <PauseDialog />

                    {isWritableVisible && isWritableMobile && (
                        <style>{`
                            div#${colorPaletteUiId} {
                                left: ${renderPathWidth}px !important;
                                width: ${whiteAreaWidth}px !important;
                            }
                            div#${colorPaletteInnerId} {
                                left: ${writableLeftOverride}px !important;
                            }
                        `}</style>
                    )}

                    <TokenFountain
                        pathWidth={pathBaseSrcWidth}
                        activityWidth={renderActivityWidth}
                    />

                    <div
                        className="path-ui-activity-wrapper open"
                        id="path-ui-activity-wrapper"
                        style={{
                            position: "absolute",
                            top: 0,
                            left: activityWrapperLeft,
                            width: renderActivityWidth,
                            height: renderActivityHeight,
                        }}
                    >
                        <div
                            id="media-wrapper"
                            style={{
                                background: "#000000",
                                width: renderActivityWidth,
                                height: renderActivityHeight
                            }} 
                        >
                            <div id="media-inner">
                                <div
                                    id="phaser-content"
                                    className="phaser-content-div phaser-content-div-open" 
                                    style={{
                                        width: renderActivityWidth,
                                        height: renderActivityHeight
                                    }}
                                >
                                    <Activity skin={ skin } />
                                </div>
                            </div>
                        </div>

                        {(shouldDisplaySpinner()) && 
                            <div
                                style={{
                                    position: "absolute",
                                    width: "56px",
                                    height: "56px",
                                    top:  "50%",
                                    left:  "50%",
                                    transform:  "translate(-50%, -50%)",
                                    color: "#555555",
                                    zIndex: 1060
                                }}
                            >
                                <i 
                                    id="media-spinner"
                                    className="fa fa-circle-o-notch fa-spin"
                                    style={{
                                        fontSize: "56px",
                                        width: "56px",
                                        height: "56px",
                                        color: "#ffffff",
                                        zIndex: 1060
                                    }}
                                />
                            </div>
                        }
                    </div>

                    <div ref={wrapperRef} id="sz-buttons-wrapper" style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: innerWidth,
                        height: miniContentHeight,
                        background: "transparent",
                        zIndex: 1120,
                        pointerEvents: "none",
                        opacity: areGameButtonsVisible ? 1 : 0,
                        overflow: "hidden"
                    }}>
                        {activityType !== ActivityType.None ? <SZGameButtons activityType={activityType} /> : null }
                    </div>

                </div>}

                { isFinalRewardTime && <FinalRewardVideoPlayer
                    videos={[skin.reward.video]}
                    node={skin.reward.node}
                /> }
            </div>
        );
    }

    if (isLoaded) {
        const scene = parentData?.settings?.szLearningPathData?.scene
            ? parentData.settings.szLearningPathData.scene
            : SceneType.Younger;
        
        const character = parentData?.settings?.sz_profiles?.szPhaserProfile?.active?.characterName 
            ? parentData.settings.sz_profiles.szPhaserProfile.active.characterName 
            : CharacterType.Charlie;

        // if(debugSubject === SubjectType.Debug) {
        //     const allSubjects = Object.values(SubjectType);
        //     const rndSubject = allSubjects[Math.floor(Math.random() * allSubjects.length)];
        //     dispatch(setDebugSubject(rndSubject));
        // }
        // if(debugScene === SceneType.Debug) {
        //     let rndScene = ((Math.random() < 0.5) ? SceneType.Younger : SceneType.Older);
        //     dispatch(setDebugScene(rndScene as SceneType));
        // }
        // if(debugCharacter === CharacterType.Debug) {
        //     const allCharacters = Object.values(CharacterType);
        //     const rndChar = allCharacters[Math.floor(Math.random() * allCharacters.length)];
        //     dispatch(setDebugCharacter(rndChar));
        // }

        // Debug random layout:
        // if(debugScene === SceneType.Debug || debugCharacter === CharacterType.Debug){
        //     debugScene = SceneType.Younger;
        //     debugCharacter = CharacterType.Charlie;
        // }
        // return screenLayout(config.skins.style[debugScene][debugCharacter]);

        // Debug specific layout:
        // return screenLayout(config.skins.style[SceneType.Older][CharacterType.Socrates]);

        return screenLayout(miniConfig.skins.style[scene][character]);
    } else {
        return null;
    }
}

export default Mini;

